import { useMemo } from 'react';

import { useEventCapacity } from 'features/common/hooks/useEventCapacity';
import type { CapacityPool } from 'features/common/hooks/useEventCapacity';
import { compareByTier, paidCount } from './inventoryUtils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Genuine per-type reserve (comp/keep). NOT a shared-capacity pool. */
export interface InventoryHold {
	amount: number;
}

export interface InventoryColumn {
	typeId: number;
	name: string;
	description: string;
	initialPrice: number;
	payWhatYouWant: boolean;
	payout: number; // PAID × effectivePrice (paid = issued − comped; comps are $0 revenue; 0 for free; uses maxPrice for PWYW)
	issued: number; // paid sold + comped + claimed (time-series fallback for free/comped)
	compedCount: number; // comped tickets for this type (from useCompedInventory; 0 when unavailable)
	hold: InventoryHold | null; // per-type reserve only — distinct from pool membership
	poolName: string | null; // shared-capacity pool this type draws from (null = standalone)
	poolId: number | null;
	available: number; // sellable capacity, pool-capped: min(typeRemaining, poolRemaining) (0 when expired)
	expired: number; // unsold capacity past offSellingTime (pool-capped; 0 when on sale)
	poolBlocked: number; // pooled tier's own leftover the shared pool can't sell (the "Pool full" slice); 0 standalone
	trueCapacity: number; // amount + holds = total physical capacity
	ceiling: number; // trueCapacity × effectivePrice (0 for free; uses maxPrice for PWYW)
}

export interface InventorySummary {
	totalIssued: number; // Σ per-type resolved issued (totals.issued) — reconciles with the donut segments
	totalSold: number;
	totalComped: number;
	totalHolds: number;
	totalAvailable: number;
	totalExpired: number;
	totalCapacity: number; // Σ trueCapacity (physical) — still backs the revenue ceiling
	effectiveCapacity: number; // pool-capped count denominator (summary donut center Y); = totalCapacity when no pools
	totalPayout: number;
	summaryCeiling: number;
	// Pool roll-up (two-mode rendering). Empty / 0 when the event has no pools.
	pools: CapacityPool[];
	totalPoolAllocated: number;
	totalPoolSold: number;
	totalPoolRemaining: number;
}

export interface TicketInventoryData {
	columns: InventoryColumn[]; // sorted: priced desc → PWYW → free
	summary: InventorySummary;
	pools: CapacityPool[]; // convenience mirror of summary.pools (two-mode layout)
	hasPools: boolean;
	isSeated: boolean; // seated event (seats.io) — drives section copy/tooltips
	isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Hook — thin adapter over the shared useEventCapacity source of truth.
// Owns only presentation concerns: tier sort + price-derived payout/ceiling +
// rolling the capacity totals/pools into the inventory-shaped summary.
// All capacity/availability/holds/pool logic lives in useEventCapacity.
// ---------------------------------------------------------------------------

const EMPTY_SUMMARY: InventorySummary = {
	totalIssued: 0,
	totalSold: 0,
	totalComped: 0,
	totalHolds: 0,
	totalAvailable: 0,
	totalExpired: 0,
	totalCapacity: 0,
	effectiveCapacity: 0,
	totalPayout: 0,
	summaryCeiling: 0,
	pools: [],
	totalPoolAllocated: 0,
	totalPoolSold: 0,
	totalPoolRemaining: 0,
};

export function useTicketInventoryData(compedByType: Record<string, number> = {}): TicketInventoryData {
	const { types, pools, hasPools, isSeated, totals, isLoading } = useEventCapacity(compedByType);

	const columns = useMemo((): InventoryColumn[] => {
		return [...types].sort(compareByTier).map((t): InventoryColumn => {
			const effectivePrice = t.payWhatYouWant ? t.maxPrice : t.initialPrice;
			// Revenue is paid tickets only — comped seats went out at $0, so they
			// must not inflate the estimate (issued includes comps; subtract them).
			const payout = effectivePrice > 0 ? paidCount(t.sold, t.compedCount) * effectivePrice : 0;
			const ceiling = effectivePrice > 0 ? t.trueCapacity * effectivePrice : 0;
			return {
				typeId: t.typeId,
				name: t.name,
				description: t.description,
				initialPrice: t.initialPrice,
				payWhatYouWant: t.payWhatYouWant,
				payout,
				issued: t.sold,
				compedCount: t.compedCount,
				hold: t.holds > 0 ? { amount: t.holds } : null,
				poolName: t.poolName,
				poolId: t.poolId,
				available: t.available,
				expired: t.expired,
				poolBlocked: t.poolBlocked,
				trueCapacity: t.trueCapacity,
				ceiling,
			};
		});
	}, [types]);

	const summary = useMemo((): InventorySummary => {
		if (columns.length === 0) return EMPTY_SUMMARY;
		const totalPayout = columns.reduce((s, c) => s + c.payout, 0);
		const summaryCeiling = columns.reduce((s, c) => s + c.ceiling, 0);
		return {
			// Canonical issued = Σ per-type resolved issued (totals.issued), so the summary center reconciles
			// with the per-type/pool donut segments below it. NOT totals.sold + totals.comped (show-level
			// Highlights scalars), which can disagree by the backend two-writer delta — docs §1.
			totalIssued: totals.issued,
			totalSold: totals.sold,
			totalComped: totals.comped,
			totalHolds: totals.holds,
			totalAvailable: totals.available,
			totalExpired: totals.expired,
			totalCapacity: totals.capacity,
			effectiveCapacity: totals.effectiveCapacity,
			totalPayout,
			summaryCeiling,
			pools,
			totalPoolAllocated: totals.poolAllocated,
			totalPoolSold: totals.poolSold,
			totalPoolRemaining: totals.poolRemaining,
		};
	}, [columns, totals, pools]);

	return { columns, summary, pools, hasPools, isSeated, isLoading };
}
